import {
  Box,
  Collapse,
  Drawer,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import { Icon, Modal } from 'components'
import { inject, observer } from 'mobx-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, Location } from 'react-router'
import { UserType } from 'types'
import { FeedbackForm, FooterListItem, IMainSidebarProps, SidebarMenuItem } from 'views'
import { Logo } from '../../../assets/svg/logo'
import { theme } from '../../config'
import {  Pages } from '../../routes'


export const DesktopMainSidebar = inject('routingStore', 'authStore')(observer((props: IMainSidebarProps): JSX.Element => {
  const location: Location      = useLocation();

  const { t }                         = useTranslation();
  const { routingStore, authStore }   = props;
  
  const [selectedIdx, setSelectedIdx] = React.useState<number>(undefined);
  const [openModal,   setOpenModal]   = React.useState<boolean>(false);

  const menuItems: SidebarMenuItem[] = [
    {
      listItemText: t('library'),
      icon:         'book',
      path:         Pages.Library,
      userTypes:    [UserType.Participant, UserType.Manager],
    },{
      listItemText: t('user_plural'),
      icon:         'users',
      path:         Pages.Users,
      userTypes:    [UserType.Manager],
    },{
      listItemText: t('customer_plural'),
      icon:         'users',
      path:         Pages.Customers,
      userTypes:    [UserType.Admin],
      showNested:   routingStore.getCustomerShowNested,
      nestedItems: [
        {
          listItemText: t('user_plural'),
          icon:         'users',
          path:         Pages.Customers,
          routingParams: routingStore.getCustomerRoutingParams('users'),
          userTypes:    [UserType.Admin],
          selected:      routingStore?.params?.view === 'users'
        },{
          listItemText:  t('programs'),
          icon:          'programs',
          path:          Pages.Customers,
          routingParams: routingStore.getCustomerRoutingParams('programs'),
          userTypes:     [UserType.Admin],
          selected:      routingStore?.params?.view === 'programs'
        }
      ]
    },{
      listItemText: t('statistic'),
      icon:         'statistics',
      path:         Pages.Statistics,
      userTypes:    [UserType.Manager, UserType.Analyst],
    },{
      listItemText: t('clients'),
      icon:         'users-circle',
      path:         Pages.Licenses,
      userTypes:    [UserType.Analyst],
    },{
      listItemText: t('courses_evaluations'),
      icon:         'download',
      path:         Pages.Evaluations,
      userTypes:    [UserType.Analyst, UserType.Admin],
    },{
      listItemText: t('packages_sidebar'),
      icon:         'users-circle',
      path:         Pages.Packages,
      userTypes:    [UserType.Editor, UserType.Manager],
    },{
      listItemText: t('programs'),
      icon:         'programs',
      path:         Pages.Programs,
      userTypes:    [UserType.Editor, UserType.Manager],
    },{
      listItemText: t('courses'),
      icon:         'book',
      path:         Pages.Courses,
      userTypes:    [UserType.Editor, UserType.Manager],
    },{
      listItemText: t('roles'),
      icon:         'book',
      path:         Pages.System,
      userTypes:    [UserType.Admin],
    },{
      listItemText: t('certificates'),
      icon:         'ribbon',
      path:         Pages.Certificates,
      userTypes:    [],
    },{
      listItemText: t('faq'),
      icon:         'multiple-chat',
      path:         Pages.Faq,
      userTypes:    [UserType.Participant, UserType.Manager],
    },
    // {
    //   listItemText: 'Style guide',
    //   path: Pages.StyleGuide,
    // },
  ];

  const footerItems: FooterListItem[] = [
    {
      listItemText: t("webinar_add_title"),
      icon:          "info",
      url:           "https://www.bginstitute.se/event/?utm_medium=referral&utm_source=lms&utm_campaign=aku&utm_content=web",
      tooltip:       t("webinar_tooltip"),
      color:         '#8DA6BB',
      textColor:     theme.palette.black.main,
    },{
      listItemText: t("other_courses_add_title"),
      icon:          "info",
      url:           "https://www.bginstitute.se/utbildningar/bank-finans-forsakring/?utm_medium=referral&utm_source=lms&utm_campaign=aku&utm_content=kurs",
      tooltip:       t("other_courses_tooltip"),
      color:         '#A6C0D5',
      textColor:     theme.palette.black.main
    }
  ];

  return (
    <Box>
      <Drawer
        sx={{ width: theme.drawerWidth, flexShrink: 0 }}
        variant='permanent'
        anchor='left'
      >
        <Box
          style={{ cursor: 'pointer' }}
          display='flex'
          flexDirection='row'
          justifyContent={'center'}
          padding={0}
          alignItems='center'
          marginTop={'2.4rem'}
          color={theme.palette.white.main}
        >
          <Logo width={148} />
        </Box>
        <List
          component='nav'
          sx={{
            color: theme.palette.primary.contrastText,
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            marginTop: { sx: '0', sm: '4rem' },
          }}
        >
          {menuItems
            .filter(
              (i) =>
                !i.userTypes || i.userTypes.includes(authStore.getCurUserType)
            )
            .map((i, idx) => {
              if (i.nestedItems)
                return (
                  <React.Fragment key={i.listItemText + i.path}>
                    <ListItem
                      key={i.listItemText + i.path}
                      sx={{
                        padding: '0px 16px',
                      }}
                    >
                      <ListItemButton
                        selected={location.pathname.endsWith(i.path)}
                        key={i.listItemText + i.path}
                        sx={{ borderRadius: '6px' }}
                        onClick={() => {
                          setSelectedIdx(undefined)
                          routingStore.push(i.path, i.routingParams)
                        }}
                      >
                        {i.icon !== undefined && (
                          <ListItemIcon>
                            <Icon iconId={i.icon} size='size-24' />
                          </ListItemIcon>
                        )}
                        <ListItemText primary={i.listItemText} />
                      </ListItemButton>
                    </ListItem>
                    <Collapse
                      unmountOnExit
                      key={i.listItemText}
                      in={
                        i.showNested !== undefined
                          ? i.showNested
                          : selectedIdx === idx
                      }
                      timeout='auto'
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <List component='div' disablePadding>
                        {i.nestedItems?.map((sub) => (
                          <ListItemButton
                            key={'sub_' + sub.listItemText + sub.path}
                            onClick={() => {
                              setSelectedIdx(undefined)
                              routingStore.push(sub.path, sub.routingParams)
                            }}
                            sx={{ pl: '4.3rem' }}
                            selected={
                              sub.selected === undefined
                                ? location.pathname.endsWith(sub.path)
                                : sub.selected
                            }
                          >
                            {sub.icon !== undefined && (
                              <ListItemIcon>
                                <Icon iconId={sub.icon} size='size-24' />
                              </ListItemIcon>
                            )}
                            <ListItemText primary={sub.listItemText} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                )
              else
                return (
                  <ListItem
                    key={i.listItemText + i.path}
                    sx={{
                      padding: '0px 16px',
                    }}
                  >
                    <ListItemButton
                      selected={location.pathname.endsWith(i.path)}
                      key={i.listItemText}
                      sx={{ borderRadius: '6px' }}
                      onClick={() => {
                        setSelectedIdx(undefined)
                        routingStore.push(i.path, i.routingParams)
                      }}
                    >
                      {i.icon !== undefined && (
                        <ListItemIcon>
                          <Icon iconId={i.icon} size='size-24' />
                        </ListItemIcon>
                      )}
                      <ListItemText primary={i.listItemText} />
                    </ListItemButton>
                  </ListItem>
                )
            })}
        </List>
        <List
          component='nav'
          sx={{
            color: theme.palette.primary.contrastText,
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            marginTop: { sx: 'auto', sm: 'auto' },
          }}
        >
          {footerItems.map((i) => (
            <ListItem key={i.listItemText} sx={{ backgroundColor: i.color }}>
              <ListItemText>
                <Link
                  sx={{ textDecoration: 'none', color: i.textColor }}
                  color='inherit'
                  href={i.url}
                >
                  {i.listItemText}
                </Link>
              </ListItemText>
              {i.icon !== undefined && (
                <Tooltip title={i.tooltip}>
                  <ListItemIcon style={{ justifyContent: 'center' }}>
                  <Icon iconId={i.icon} color={i.textColor} size='size-20' />
                  </ListItemIcon>
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <FeedbackForm
          onCancel={() => setOpenModal(false)}
          onSend={async (message) => {
            return await props.authStore.submitFeedback(message)
          }}
        />
      </Modal>
    </Box>
  )
}));
